/**
 * ChartOfAccountsTab — plan comptable style Sage 50 / Acomba (en-têtes · comptes · totaux) avec soldes à une date.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Download, Pencil, Plus, Scale, Settings2, Sparkles, Search } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Empty, Pill, TYPE_LABEL, errMsg, inputCls, money, today, useGlBase } from "./_shared";
import AccountDialog from "./AccountDialog";
import GlSettingsDialog from "./GlSettingsDialog";
import OpeningBalancesDialog from "./OpeningBalancesDialog";
import QboImportDialog from "../books/qbo/QboImportDialog";

export default function ChartOfAccountsTab({ settings, refresh, goTab }) {
  const base = useGlBase();
  const books = settings?.scope === "books";
  const [data, setData] = useState(null);
  const [asOf, setAsOf] = useState(today());
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [dialog, setDialog] = useState(null); // {type: "account", account} | {type: "settings"} | {type: "opening"} | {type: "qbo"}
  const [busy, setBusy] = useState(false);
  const [sp, setSp] = useSearchParams();

  // Retour du consentement Intuit (Batch #14.297) : ?qbo=connected&company=… → ouvre la reprise des soldes ; ?qbo=error&msg=…
  useEffect(() => {
    const q = sp.get("qbo");
    if (!q) return;
    if (q === "connected") { toast.success(`QuickBooks connecté${sp.get("company") ? ` — ${sp.get("company")}` : ""}`); setDialog({ type: "qbo" }); }
    else toast.error(`Connexion QuickBooks refusée : ${sp.get("msg") || "erreur"}`);
    const next = new URLSearchParams(sp); next.delete("qbo"); next.delete("company"); next.delete("msg"); setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const load = useCallback(async () => {
    try { const r = await api.get(`${base}/accounts`, { params: { as_of: asOf } }); setData(r.data); }
    catch (e) { toast.error(errMsg(e, "Chargement du plan comptable impossible")); }
  }, [asOf, base]);
  useEffect(() => { load(); }, [load]);

  const installTemplate = async (mode) => {
    if (mode === "replace" && !window.confirm("Remplacer tout le plan comptable par le modèle ? (refusé s'il existe des écritures)")) return;
    setBusy(true);
    try {
      const r = await api.post(`${base}/accounts/install-template`, { mode });
      toast.success(`${r.data.added} compte(s) ajouté(s) — ${r.data.total} au total`);
      load(); refresh();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const onSaved = () => { setDialog(null); load(); refresh(); };
  const roleByAccount = Object.fromEntries(Object.entries(data?.roles || {}).map(([role, id]) => [id, role]));
  const rolesCatalog = settings?.roles_catalog || {};
  const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const nq = norm(q.trim());
  const items = (data?.items || []).filter((a) => (showInactive || a.is_active !== false || a.kind !== "A")
    && (!nq || norm(`${a.number} ${a.name} ${a.description || ""} ${rolesCatalog[roleByAccount[a.id]] || ""} ${TYPE_LABEL[a.type] || ""}`).includes(nq)));
  const empty = data && !data.items?.length;

  return (
    <div className="space-y-4" data-testid="chart-of-accounts-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="N° ou nom du compte…" className={`${inputCls} pl-9 w-56`} data-testid="coa-search" />
          </div>
          <label className="text-xs text-slate-500 flex items-center gap-1.5">Soldes au
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="coa-as-of" />
          </label>
          <label className="text-xs text-slate-500 flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-emerald-600" data-testid="coa-show-inactive" /> Inactifs
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Btn tone="slate" icon={Settings2} onClick={() => setDialog({ type: "settings" })} testId="coa-settings-btn">Rôles & réglages</Btn>
          <Btn tone="slate" icon={Scale} onClick={() => setDialog({ type: "opening" })} disabled={empty} testId="coa-opening-btn">Soldes d'ouverture</Btn>
          {!empty && <Btn tone="slate" icon={Download} onClick={() => installTemplate("missing")} busy={busy} testId="coa-install-missing-btn" title="Ajoute les comptes du modèle qui manquent">Compléter avec le modèle</Btn>}
          <Btn icon={Plus} onClick={() => setDialog({ type: "account", account: null })} testId="coa-new-account-btn">Nouveau compte</Btn>
        </div>
      </div>

      {empty && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 text-center" data-testid="coa-empty-banner">
          <Sparkles className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
          <h3 className="font-bold text-emerald-900">Plan comptable vide</h3>
          <p className="text-sm text-emerald-800 mt-1 max-w-xl mx-auto">
            {books
              ? "Installe le modèle simplifié (une soixantaine de comptes à 4 chiffres : 1000 actif · 2000 passif · 3000 capitaux · 4000 revenus · 5000 dépenses) avec les rôles préconfigurés (banque, clients, fournisseurs, TPS/TVQ…). Tu pourras tout renommer ou ajouter ensuite."
              : "Installe le modèle de 77 comptes (numérotation à 4 chiffres style Sage 50 / Acomba : 1000 actif · 2000 passif · 3000 capitaux · 4000 revenus · 5000 dépenses) avec les rôles préconfigurés (banque, clients, fournisseurs, TPS/TVQ…). Tu pourras tout renommer ou ajouter ensuite."}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            <Btn icon={Download} onClick={() => installTemplate("missing")} busy={busy} testId="coa-install-template-btn">Installer le modèle</Btn>
            <Btn tone="slate" icon={Plus} onClick={() => setDialog({ type: "account", account: null })} testId="coa-empty-new-account-btn">Créer mes comptes à la main</Btn>
          </div>
        </div>
      )}

      {!empty && data && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-20">N°</th>
                <th className="text-left px-3 py-2">Compte</th>
                <th className="text-left px-3 py-2 w-32 hidden md:table-cell">Type</th>
                <th className="text-right px-3 py-2 w-36">Solde</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((a) => {
                const role = roleByAccount[a.id];
                const rowCls = a.kind === "H" ? "bg-slate-50/80 font-bold text-slate-800 uppercase text-xs" : a.kind === "T" ? "italic font-semibold text-slate-700 border-t-2 border-slate-200" : a.is_active === false ? "text-slate-400" : "";
                return (
                  <tr key={a.id} className={`${rowCls} hover:bg-emerald-50/40 transition`} data-testid={`coa-row-${a.number}`}>
                    <td className="px-3 py-2 font-mono text-xs">{a.number}</td>
                    <td className={`px-3 py-2 ${a.kind === "A" ? "pl-6" : ""}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{a.name}</span>
                        {role && <Pill cls="bg-emerald-100 text-emerald-800 border-emerald-200" title="Rôle du compte" testId={`coa-role-${a.number}`}>{rolesCatalog[role] || role}</Pill>}
                        {a.kind === "A" && a.bank_reconcile && <Pill cls="bg-teal-100 text-teal-800 border-teal-200" title="Compte bancaire à rapprocher (tuile Banque)" testId={`coa-bank-reconcile-${a.number}`}>Rapprochement</Pill>}
                        {a.kind === "A" && a.is_active === false && <Pill cls="bg-slate-100 text-slate-500 border-slate-200">Inactif</Pill>}
                      </div>
                      {a.description && <div className="text-[11px] text-slate-400 font-normal normal-case">{a.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500 hidden md:table-cell">{a.kind === "A" ? TYPE_LABEL[a.type] : ""}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums" data-testid={`coa-balance-${a.number}`}>
                      {a.kind === "H" ? "" : a.kind === "A" && !a.has_entries && !a.balance ? <span className="text-slate-300">—</span> : money(a.balance)}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => setDialog({ type: "account", account: a })} className="text-slate-400 hover:text-emerald-700 p-1" title="Modifier" data-testid={`coa-edit-${a.number}`}><Pencil className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
              {!items.length && <tr><td colSpan={5}><Empty icon={BookOpen} text="Aucun compte ne correspond" /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {!empty && data && (
        <p className="text-[11px] text-slate-400">
          Soldes présentés selon le côté normal du compte (débiteur pour l'actif et les dépenses, créditeur pour le passif, les capitaux et les revenus). Les lignes « Total » additionnent les comptes depuis l'en-tête précédent.
          <button className="underline ml-1" onClick={() => goTab("grand-livre", "grand-livre")} data-testid="coa-goto-ledger">Voir le grand livre d'un compte</button>.
        </p>
      )}

      {dialog?.type === "account" && <AccountDialog account={dialog.account} onClose={() => setDialog(null)} onSaved={onSaved} />}
      {dialog?.type === "settings" && <GlSettingsDialog settings={settings} accounts={data?.items || []} onClose={() => setDialog(null)} onSaved={onSaved} onOpenQbo={() => setDialog({ type: "qbo" })} />}
      {dialog?.type === "qbo" && <QboImportDialog masterId={settings.master_id} accounts={data?.items || []} settings={settings} onClose={() => setDialog(null)} onDone={() => { load(); refresh(); }} />}
      {dialog?.type === "opening" && <OpeningBalancesDialog settings={settings} accounts={data?.items || []} onClose={() => setDialog(null)} onSaved={onSaved} />}
    </div>
  );
}
